import {
  Autocomplete,
  Button,
  ButtonGroup,
  Checkbox,
  Input,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuestionnaireApi } from '../../api/demographics';
import { useEffect, useState } from 'react';
import GuestBase from './guestBase';
import { useNavigate } from 'react-router-dom';
import './guest.css'

const DemographicsAutocomplete = ( { options, selected, onChange } ) => (
  <Autocomplete
    size="small"
    disablePortal
    options={ options }
    getOptionLabel={ option => option.name }
    value={ selected }
    onChange={ ( event, newValue ) => {
      onChange( newValue );
    } }
    renderInput={ params => (
      <TextField
        { ...params }
        sx={ {
          '& .MuiInputBase-root': {
            '& > fieldset': {
              borderWidth: 0,
            },
          },
          backgroundColor: '#EDF9F9',
          borderRadius: '10px',
        } }
      />
    ) }
  />
);

export const DemographicsButtonToggle = ( { options, selected, onChange, small = false } ) => (
  <ButtonGroup
    variant="contained"
    aria-label="primary button group"
    fullWidth={ !small }
    sx={ {
      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
        borderColor: 'lightgray',
      },
    } }
  >
    { options.map( option => (
      <Button
        key={ option.id }
        sx={ {
          borderRadius: '20px',
          backgroundColor: selected === option.id ? 'primary' : '#EDF9F9',
          color: selected === option.id ? 'white' : 'black',
          p: small ? 0 : null,
        } }
        onClick={ () => {
          onChange( option.id );
        } }
      >
        { option.name }
      </Button>
    ) ) }
  </ButtonGroup>
);

const inchesToCm = inches => inches * 2.54;
const cmToInches = cm => cm / 2.54;
const lbsToKg = lbs => lbs * 0.453592;
const kgToLbs = kg => kg / 0.453592;

const ImperialHeightInput = ( { height, setHeight } ) => {
  const [ feet, setFeet ] = useState( null );
  const [ inches, setInches ] = useState( null );

  useEffect( () => {
    if ( height && ( feet === null || inches === null ) ) {
      setFeet( Math.floor( height / 12 ) );
      setInches( height % 12 );
    }
  }, [ height ] );

  useEffect( () => {
    if ( feet !== null && inches !== null ) {
      setHeight( feet * 12 + inches );
    }
  }, [ feet, inches ] );

  console.log( height, feet, inches );

  return (
    <Stack direction="row" spacing={ 1 }>
      <Input
        size="small"
        variant="standard"
        type="number"
        value={ feet }
        onChange={ event => setFeet( parseInt( event.target.value ) ) }
        endAdornment={
          <InputAdornment position="end" sx={ { color: 'black' } }>
            <Typography sx={ { fontWeight: 500 } }>ft</Typography>
          </InputAdornment>
        }
      />
      <Input
        size="small"
        variant="standard"
        type="number"
        value={ inches }
        onChange={ event => setInches( parseInt( event.target.value ) ) }
        endAdornment={
          <InputAdornment position="end" sx={ { color: 'black' } }>
            <Typography sx={ { fontWeight: 500 } }>in</Typography>
          </InputAdornment>
        }
      />
    </Stack>
  );
};

export const GuestDemographics = () => {
  const navigate = useNavigate();

  const [ ethnicities, setEthnicities ] = useState( [] );
  const [ genders, setGenders ] = useState( [] );
  const api = useQuestionnaireApi();

  const [ dateOfBirth, setDateOfBirth ] = useState( null );
  const [ gender, setGender ] = useState( null );
  const [ heightIn, setHeightIn ] = useState( null );
  const [ heightCm, setHeightCm ] = useState( null );
  const [ heightImperialUnits, setHeightImperialUnits ] = useState( false );
  const [ weightKg, setWeightKg ] = useState( null );
  const [ weightLbs, setWeightLbs ] = useState( null );
  const [ weightImperialUnits, setWeightImperialUnits ] = useState( false );
  const [ ethnicity, setEthnicity ] = useState( null );
  const [ smoker, setSmoker ] = useState( false );
  const [ diabetes, setDiabetes ] = useState( 3 );
  const [ pregnant, setPregnant ] = useState( false );
  const [ bpMeds, setBpMeds ] = useState( false );
  const [ heartAttack, setHeartAttack ] = useState( false );
  const [ stroke, setStroke ] = useState( false );

  const [ agreeConditions, setAgreeConditions ] = useState( false );
  const [ agreeTerms, setAgreeTerms ] = useState( false );

  useEffect( () => {
    api.getEthnicities().then( response => {
      setEthnicities( response.data );
    } );
    api.getGenders().then( response => {
      setGenders( response.data );
    } );
  }, [] );

  useEffect( () => {
    setHeightCm( null );
    setHeightIn( null );
  }, [ heightImperialUnits ] );

  useEffect( () => {
    setWeightKg( null );
    setWeightLbs( null );
  }, [ weightImperialUnits ] );

  const handleSubmit = async () => {
    await api.postQuestionnaire(
      dateOfBirth,
      gender.id,
      ethnicity.id,
      heightImperialUnits ? inchesToCm( heightIn ) : heightCm,
      weightImperialUnits ? lbsToKg( weightLbs ) : weightKg,
      heightImperialUnits,
      weightImperialUnits,
      smoker,
      diabetes.id,
      bpMeds,
      bpMeds,
      pregnant,
      stroke,
      heartAttack,
    );

    navigate( '/guest-pre-face-scan' );
  };

  return (
    <GuestBase onPrev={ () => navigate( '/start' ) } onNext={ handleSubmit }>
      <Stack className='guest-box'>
        <Stack sx={ { p: 3 } } className='guest-wrapper'>
          <Typography
            sx={ {
              fontSize: '30px',
              fontWeight: 700,
            } }
          >
            Tell us about you.
          </Typography>
          <Typography>You'll be up and running in no time.</Typography>
          <Stack sx={ { mt: 3 } } spacing={ 2 }>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>
                Date of Birth <span style={ { fontSize: '13px' } }>(MM/DD/YYYY)</span>
              </Typography>
              <LocalizationProvider dateAdapter={ AdapterDayjs }>
                <DatePicker
                  disableFuture
                  slotProps={ {
                    textField: { size: 'small', variant: 'standard', slotProps: { inputLabel: { opacity: 1 } } },
                  } }
                  isClearable={ true }
                  value={ dateOfBirth }
                  onChange={ setDateOfBirth }
                />
              </LocalizationProvider>
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>What was your sex at birth?</Typography>
              <DemographicsAutocomplete options={ genders } selected={ gender } onChange={ setGender } />
            </Stack>
            <Stack direction="row" spacing={ 2 }>
              <Stack sx={ { flex: 1 } } spacing={ 1 }>
                <Stack direction="row" spacing={ 1 } justifyContent="space-between" sx={ { width: '100%' } }>
                  <Typography
                    sx={ {
                      fontWeight: 500,
                    } }
                  >
                    Height
                  </Typography>
                  <DemographicsButtonToggle
                    options={ [
                      { id: true, name: 'ft' },
                      { id: false, name: 'cm' },
                    ] }
                    selected={ heightImperialUnits }
                    onChange={ setHeightImperialUnits }
                    small
                  />
                </Stack>
                { heightImperialUnits ? (
                  <ImperialHeightInput height={ heightIn } setHeight={ setHeightIn } />
                ) : (
                  <Input
                    size="small"
                    variant="standard"
                    type="number"
                    value={ heightCm }
                    onChange={ event => setHeightCm( event.target.value ) }
                    endAdornment={
                      <InputAdornment position="end" sx={ { color: 'black' } }>
                        <Typography sx={ { fontWeight: 500 } }>cm</Typography>
                      </InputAdornment>
                    }
                  />
                ) }
              </Stack>
              <Stack sx={ { flex: 1 } } spacing={ 1 }>
                <Stack direction="row" spacing={ 1 } justifyContent="space-between" sx={ { width: '100%' } }>
                  <Typography
                    sx={ {
                      fontWeight: 500,
                    } }
                  >
                    Weight
                  </Typography>
                  <DemographicsButtonToggle
                    options={ [
                      { id: true, name: 'lbs' },
                      { id: false, name: 'kg' },
                    ] }
                    selected={ weightImperialUnits }
                    onChange={ setWeightImperialUnits }
                    small
                  />
                </Stack>
                <Input
                  size="small"
                  variant="standard"
                  type="number"
                  value={ weightImperialUnits ? weightLbs : weightKg }
                  onChange={ event => {
                    if ( weightImperialUnits ) {
                      setWeightLbs( event.target.value );
                    } else {
                      setWeightKg( event.target.value );
                    }
                  } }
                  endAdornment={
                    <InputAdornment position="end" sx={ { color: 'black' } }>
                      <Typography sx={ { fontWeight: 500 } }>{ weightImperialUnits ? 'lbs' : 'kg' }</Typography>
                    </InputAdornment>
                  }
                />
              </Stack>
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>What is your ethnicity?</Typography>
              <DemographicsAutocomplete options={ ethnicities } selected={ ethnicity } onChange={ setEthnicity } />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Do you smoke?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ] }
                selected={ smoker }
                onChange={ setSmoker }
              />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Are you diabetic?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: 1, name: 'Type 1' },
                  { id: 2, name: 'Type 2' },
                  { id: 3, name: 'No' },
                ] }
                selected={ diabetes }
                onChange={ setDiabetes }
              />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Are you pregnant?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ] }
                selected={ pregnant }
                onChange={ setPregnant }
              />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Do you take blood pressure medication?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ] }
                selected={ bpMeds }
                onChange={ setBpMeds }
              />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Have you ever had a heart attack?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ] }
                selected={ heartAttack }
                onChange={ setHeartAttack }
              />
            </Stack>
            <Stack spacing={ 1 }>
              <Typography sx={ { fontWeight: 500 } }>Have you ever had a stroke?</Typography>
              <DemographicsButtonToggle
                options={ [
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ] }
                selected={ stroke }
                onChange={ setStroke }
              />
            </Stack>
            <Stack
              direction="row"
              spacing={ 1 }
              sx={ {
                bgcolor: '#edf9f9',
                p: 1.5,
                borderRadius: '10px',
              } }
            >
              <Checkbox
                sx={ { color: 'white' } }
                icon={
                  <svg width="24" height="24" fill="none">
                    <rect fill="#fff" width="24" height="24" rx="2" filter="drop-shadow(3px 3px 2px rgba(0, 0, 0)" />
                  </svg>
                }
                checked={ agreeConditions }
                onChange={ event => setAgreeConditions( event.target.checked ) }
              />
              <Typography sx={ { fontSize: '12px', fontWeight: 500 } }>
                I understand the information provided in this assessment is not intended for the diagnosis, treatment, or
                prevention of a disease or condition, but rather to provide me with general information about my symptoms.
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={ 1 }
              sx={ {
                bgcolor: '#edf9f9',
                p: 1.5,
                borderRadius: '10px',
              } }
              alignItems="center"
            >
              <Checkbox
                sx={ { color: 'white' } }
                icon={
                  <svg width="24" height="24" fill="none">
                    <rect fill="#fff" width="24" height="24" rx="2" filter="drop-shadow(3px 3px 2px rgba(0, 0, 0)" />
                  </svg>
                }
                checked={ agreeTerms }
                onChange={ event => setAgreeTerms( event.target.checked ) }
              />
              <Typography sx={ { fontSize: '12px', fontWeight: 500 } }>
                I have read and understand the terms of use and privacy policy.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

    </GuestBase>
  );
};
